import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export type McpScope = 'agents:invoke' | 'agents:write' | 'agents:publish';

export interface McpToken {
  tokenId: string;
  name: string;
  orgId: string;
  userId: string;
  applicationId: string;
  tokenSuffix: string;
  scopes: McpScope[];
  status: 'active' | 'revoked';
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string | null;
  token?: string;
}

export interface McpAuditEvent {
  auditId: string;
  tokenId: string;
  toolName?: string | null;
  operation: string;
  resultStatus: string;
  agentName?: string | null;
  createdAt?: string | null;
  errorCode?: string | null;
}

export interface CreateMcpTokenRequest {
  name: string;
  applicationId: string;
  scopes: McpScope[];
}

export const mcpAccessApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMcpTokens: builder.query<McpToken[], void>({
      query: () => '/portal/mcp/tokens',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ tokens: McpToken[] }>(response);
        return normalized.tokens ?? [];
      },
      providesTags: ['McpToken'],
    }),

    createMcpToken: builder.mutation<McpToken, CreateMcpTokenRequest>({
      query: (body) => ({
        url: '/portal/mcp/tokens',
        method: 'POST',
        body: {
          name: body.name,
          application_id: body.applicationId,
          scopes: body.scopes,
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<McpToken>(response),
      invalidatesTags: ['McpToken'],
    }),

    revokeMcpToken: builder.mutation<{ tokenId: string; status: string }, string>({
      query: (tokenId) => ({
        url: `/portal/mcp/tokens/${tokenId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['McpToken', 'McpAudit'],
    }),

    getMcpAudit: builder.query<McpAuditEvent[], void>({
      query: () => '/portal/mcp/audit?limit=25',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ events: McpAuditEvent[] }>(response);
        return normalized.events ?? [];
      },
      providesTags: ['McpAudit'],
    }),
  }),
});

export const {
  useGetMcpTokensQuery,
  useCreateMcpTokenMutation,
  useRevokeMcpTokenMutation,
  useGetMcpAuditQuery,
} = mcpAccessApi;
